// Sala de cierre: tablero del dueño con un análisis de IA por ticket enfocado en
// CERRARLO. Solo visible/usable por el dueño (gateo en nav + ruta + backend).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TextoConTE from "@/components/TextoConTE";
import { ApiError, api, type SalaCierre as Sala, type TicketAnalisis } from "@/lib/api";
import { copiarTexto } from "@/lib/copiar";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaSalaCierre } from "@/components/ayuda/vistas/salaCierre";

type Alcance = "atorados" | "todos" | "marcados";

const ALCANCES: { val: Alcance; label: string }[] = [
  { val: "atorados", label: "Atorados" },
  { val: "todos", label: "Todos los abiertos" },
  { val: "marcados", label: "Marcados 📌" },
];

const LS_KEY = "sala-cierre-marcados";

function leerMarcados(): number[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
  } catch {
    return [];
  }
}

export default function SalaCierre() {
  const [alcance, setAlcance] = useState<Alcance>("atorados");
  const [marcados, setMarcados] = useState<number[]>(leerMarcados);
  const [data, setData] = useState<Sala | null>(null);
  const [loading, setLoading] = useState(true);
  const [analizando, setAnalizando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idsParam = useMemo(
    () => (alcance === "marcados" ? marcados.join(",") : ""),
    [alcance, marcados],
  );

  const cargar = useCallback(() => {
    setLoading(true);
    setError(null);
    const q = `?alcance=${alcance}${idsParam ? `&ids=${idsParam}` : ""}`;
    api
      .get<Sala>(`/api/sala-cierre${q}`)
      .then(setData)
      .catch((e) =>
        setError(e instanceof ApiError ? e.detail : "No pude cargar la sala"),
      )
      .finally(() => setLoading(false));
  }, [alcance, idsParam]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  function toggleMarcado(id: number) {
    setMarcados((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
  }

  async function analizar() {
    setAnalizando(true);
    setError(null);
    try {
      const ids = alcance === "marcados" ? marcados : [];
      const r = await api.post<Sala>("/api/sala-cierre/analizar", { alcance, ids });
      setData(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : "No se pudo analizar");
    } finally {
      setAnalizando(false);
    }
  }

  async function analizarUno(casoId: number) {
    const t = await api.post<TicketAnalisis>(
      `/api/sala-cierre/${casoId}/analizar`,
      {},
    );
    setData((prev) => {
      if (!prev) return prev;
      const tickets = prev.tickets.map((x) => (x.caso_id === casoId ? t : x));
      return { ...prev, tickets, n_elegibles: tickets.filter((x) => x.elegible).length };
    });
  }

  const n = data?.n_elegibles ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-zinc-100">🚂 Sala de cierre</h1>
          <AyudaVista titulo="Sala de cierre" {...ayudaSalaCierre()} />
        </div>
        <p className="text-xs text-zinc-500">
          Para cada ticket, un análisis objetivo del caso (diagnóstico, qué se ha hecho,
          qué falta y el siguiente paso para cerrarlo), por párrafos. Solo tú ves esto.
        </p>
      </div>

      <div className="card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-1.5">
          {ALCANCES.map((a) => (
            <button
              key={a.val}
              type="button"
              onClick={() => setAlcance(a.val)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                alcance === a.val
                  ? "bg-accent-500/15 text-accent-300 border-accent-500/40"
                  : "border-ink-600 text-zinc-400 hover:text-white"
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={analizar}
          disabled={analizando || n === 0}
          className="btn-ghost text-sm border border-ink-600 disabled:opacity-50 w-full sm:w-auto text-center"
        >
          {analizando
            ? "Analizando…"
            : n === 0
              ? "Nada nuevo que analizar hoy"
              : `✨ Analizar (${n} nuevo${n === 1 ? "" : "s"})`}
        </button>
      </div>

      {error ? (
        <div className="card text-sm text-red-300">{error}</div>
      ) : loading || !data ? (
        <div className="card text-sm text-zinc-500">Cargando…</div>
      ) : data.tickets.length === 0 ? (
        <div className="card text-sm text-zinc-500">
          No hay tickets en este alcance.
          {alcance === "marcados" && " Marca algunos con 📌 desde «Todos» o «Atorados»."}
        </div>
      ) : (
        <div className="space-y-3">
          {data.tickets.map((t) => (
            <TicketCard
              key={t.caso_id}
              t={t}
              marcado={marcados.includes(t.caso_id)}
              onMarcar={() => toggleMarcado(t.caso_id)}
              onAnalizar={analizarUno}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const BADGE: Record<string, { txt: string; cls: string }> = {
  pendiente: { txt: "sin análisis", cls: "text-zinc-500 border-ink-600" },
  actualizable: { txt: "hay novedad", cls: "text-amber-300 border-amber-500/40" },
  fresco: { txt: "al día", cls: "text-emerald-300 border-emerald-500/40" },
};

const POS: Record<string, string> = {
  nosotros: "🛠 nosotros",
  cliente: "⏳ cliente",
  proveedor: "📦 proveedor",
};

function TicketCard({
  t,
  marcado,
  onMarcar,
  onAnalizar,
}: {
  t: TicketAnalisis;
  marcado: boolean;
  onMarcar: () => void;
  onAnalizar: (id: number) => Promise<void>;
}) {
  const b = BADGE[t.estado_analisis] ?? BADGE.pendiente;
  const [cargando, setCargando] = useState(false);
  const [errCard, setErrCard] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  async function copiar() {
    if (!t.analisis) return;
    // Con fallback: en un WebView sin Clipboard API antes no pasaba nada, en silencio.
    if (await copiarTexto(t.analisis)) {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } else {
      setErrCard("No pude copiar — selecciona el texto a mano.");
    }
  }
  async function analizar() {
    setCargando(true);
    setErrCard(null);
    try {
      await onAnalizar(t.caso_id);
    } catch (e) {
      setErrCard(e instanceof ApiError ? e.detail : "No se pudo analizar");
    } finally {
      setCargando(false);
    }
  }
  return (
    <div className="card space-y-2">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Link
            to={`/casos/${t.folio}`}
            className="font-mono text-sm text-accent-300 hover:underline"
          >
            {t.te || t.folio}
          </Link>
          <div className="text-xs text-zinc-400 break-words">
            {t.cliente} · {t.estado}{" "}
            <span className="text-zinc-600">[{POS[t.posesion] ?? t.posesion}]</span>
          </div>
          <div className="text-[11px] text-zinc-600 break-words">
            {t.dias_abierto} d abierto · {t.dias_en_estado} d en estado
            {t.imeis.length > 0 && (
              <>
                {" · "}📟 {t.imeis.slice(0, 3).join(", ")}
                {t.imeis.length > 3 ? ` +${t.imeis.length - 3}` : ""}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${b.cls}`}>
            {b.txt}
          </span>
          <button
            type="button"
            onClick={onMarcar}
            aria-pressed={marcado}
            aria-label={marcado ? "Quitar de Marcados" : "Marcar para «Marcados»"}
            className={`text-sm transition ${marcado ? "" : "opacity-40"} hover:opacity-100`}
          >
            📌
          </button>
        </div>
      </div>
      {t.analisis ? (
        <div className="border-t border-ink-700 pt-2 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-wide text-zinc-500">
              Análisis del caso
            </span>
            <button
              type="button"
              onClick={copiar}
              className="text-[11px] text-accent-300 hover:underline shrink-0"
            >
              {copiado ? "✓ Copiado" : "📋 Copiar"}
            </button>
          </div>
          <div className="rounded-lg border border-ink-700 bg-ink-800/50 px-3.5 py-3 text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap">
            <TextoConTE texto={t.analisis} />
          </div>
        </div>
      ) : (
        <div className="border-t border-ink-700 pt-2">
          <button
            type="button"
            onClick={analizar}
            disabled={cargando}
            className="btn-ghost text-xs border border-ink-600 disabled:opacity-50"
          >
            {cargando ? "Analizando…" : "✨ Analizar este ticket"}
          </button>
        </div>
      )}
      {errCard && <p className="text-[11px] text-red-300">{errCard}</p>}
      {t.generado_at && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-zinc-600">
            Generado el {new Date(t.generado_at).toLocaleString()}
          </p>
          <button
            type="button"
            onClick={analizar}
            disabled={cargando}
            className="text-[10px] text-zinc-500 hover:text-accent-300 disabled:opacity-50"
          >
            {cargando ? "…" : "↻ Re-analizar"}
          </button>
        </div>
      )}
    </div>
  );
}
